import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { debounce } from 'lodash';
import mapboxgl from 'mapbox-gl';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { ChipFilters } from '../../../common/ChipFiltersGroup';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { ResultFilter } from '../../../common/ContentFilters/ResultFilter';
import { ResultSort } from '../../../common/ContentFilters/ResultSort';
import { PortalContext } from '../../../context/PortalContext';
import { ErrorBoundary } from '../../../errors/ErrorBoundary';
import { useConfig } from '../../../frontends/ProviderSearch/context/Analytics';
import { getEnableMapPinRedesign } from '../../../frontends/ProviderSearch/routes/MapView/utils';
import { useCoverageType } from '../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import { Provider } from '../../../models/Provider';
import { ProviderLocation } from '../../../models/ProviderDetails';
import { ResponseHeaders } from '../../../models/ResponseHeaders';
import { Directions } from '../../../models/RouteDirections';
import { useMapStore } from '../../../store/useMapStore';
import { MapState } from '../../../store/useMapStore/mapStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import {
  getDistanceString,
  getDurationString,
  getNavTypeString,
  getStepDistanceString,
  getStepIcon,
} from '../../../utils/generalMap.utils';
import {
  Feature,
  clearRoute,
  escapeRegExp,
  getFeaturesArray,
  getRoute,
  getUniqueCoordinates,
  loadMap,
  loadResultLocationPins,
  loadRoute,
  loadUserLocationPin,
  removeAllMarkers,
  setMapCoords,
  setZoomLevel,
} from '../../../utils/map.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { Constants, ReverseCoverageTypesCodes } from '../../Constants';
import { mobileOnly } from '../../ConstantsStyles';
import { ClusterCarousel } from '../../MapDisplay/ClusterCarousel';
import { MapControlDesktop } from '../../MapDisplay/MapControlDesktop';
import { MapControlMobile } from '../../MapDisplay/MapControlMobile';
import { MapDirections } from '../../MapDisplay/MapDirections';
import {
  MapContainer,
  MapDirectionsContainer,
  NavigationContainer,
  NavigationContainer2,
} from '../../MapDisplay/MapDisplay.styled';
import { PortalIdentifier } from '../../Portal';

type Props = {
  closeSidePanel?: boolean;
  coords: { latitude: string; longitude: string };
  disabledPinAction?: boolean;
  fullWidth?: boolean;
  headers?: ResponseHeaders;
  locationResults?: ProviderLocation[] | FacilityLocationsResponse[];
  map: any;
  isOpenCompareShare?: boolean;
  providerPins?: Provider[];
  providerResults?: Provider[];
  routeEndCoords?: [number | null, number | null];
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  selectedItems?: any[];
  mobileMapControlChildren?: React.ReactNode;
  mobileRouteView?: boolean;
  setPopupContent?(
    content: ProviderLocation | FacilityLocationsResponse | null
  ): void;
  refToBeFocusedOnDirectionsClose?: HTMLElement;
  updatePin?(
    content: ProviderLocation | FacilityLocationsResponse | null
  ): void;
  selectLocation?: (locationId, locationLng, locationLat) => void;
  directions?: Directions;
  mobileScreen?: boolean;
  isLocationsTab?: boolean;
  previousLocation: string;
  setPreviousLocation: (a: string) => void;
  enableMapFilterSortCTA: boolean;
  expandedProviderListView: boolean;
  setExpandedProviderListView: (a: boolean) => void;
  setNavigateToDirections: (a: boolean) => void;
  skipNavigationFocus?: boolean;
  closeMapView?: () => void;
};

export const DirectionMapDisplay = ({
  coords,
  disabledPinAction = false,
  fullWidth = false,
  isOpenCompareShare = false,
  providerPins = [],
  providerResults = [],
  locationResults = [],
  map,
  closeSidePanel,
  routeEndCoords = [null, null],
  setRouteEndCoords,
  selectedItems,
  mobileMapControlChildren,
  mobileRouteView,
  setPopupContent,
  refToBeFocusedOnDirectionsClose,
  updatePin,
  selectLocation,
  directions,
  headers,
  mobileScreen = false,
  isLocationsTab = false,
  previousLocation,
  setPreviousLocation,
  enableMapFilterSortCTA,
  expandedProviderListView,
  setExpandedProviderListView,
  setNavigateToDirections,
  skipNavigationFocus = false,
  closeMapView,
}: Props) => {
  const { t } = useTranslation();
  const mapContainerTestId: string = 'map-view-container';
  const { longitude, latitude } = coords;
  const enableScreenZoom = useMediaQuery(mobileOnly);

  const mapOnPremKey = useConfig('MAPBOX_ONPREM_KEY');
  const mapOnPremURL = useConfig('MAPBOX_ONPREM_URL');
  const { portalData } = useContext(PortalContext);

  const [navType, setNavType] = useState<string>('driving-traffic');
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const [currentRoute, setCurrentRoute] = useState<number>(0);
  const [showStepInstructions, setShowStepInstructions] =
    useState<boolean>(false);
  const [sortFilterOpen, setSortFilterOpen] = useState(
    storage.session.get('sortFilterOpen')
  );
  const [allFiltersOpen, setAllFiltersOpen] = useState(
    storage.session.get('allFiltersOpen')
  );

  const [mapDirections, setMapDirections] = useState<Directions>(
    directions || {
      userPlaceName: '',
      endPlaceName: '',
      routes: [],
    }
  );

  const mapContainerRef = React.useRef<any>(null);
  const navigationContainerRef = useRef<HTMLElement>();

  const [highlightId, setHighlightId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: '' }
  );

  const [, setSelectedId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );

  const [, setMapPinCoords] = useSessionStorage<
    (string[] | (string | undefined)[])[]
  >(Constants.STORAGE_KEYS.SESSION.MAP_PIN_COORDS, []);

  const [
    enableMapEnhancements,
    enableMapViewLargeResults,
    enableMapPinRedesignFlag,
    enableMapDragRefreshResults,
    enableMapViewHideOption,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_LARGE_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_DRAG_REFRESH_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);

  const [, getUserLocation] = useUserLocation({});

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );
  const enableMapPinRedesign = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const {
    mapLatitude,
    mapLongitude,
    mapSearchRadius,
    setMapLatitude,
    setMapLongitude,
    setMapSearchRadius,
  } = useMapStore(
    useShallow((state: MapState) => ({
      mapLatitude: state.mapLatitude,
      mapLongitude: state.mapLongitude,
      mapSearchRadius: state.mapSearchRadius,
      setMapLatitude: state.setMapLatitude,
      setMapLongitude: state.setMapLongitude,
      setMapSearchRadius: state.setMapSearchRadius,
    }))
  );

  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;
  const additionalCss = isGEHAV2
    ? '$core.color.brand.100'
    : '$descriptionBlack';

  const { search, sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );

  const doSetMapCoords = (isZoomChanged: boolean) => {
    setMapCoords(
      map.current,
      mapLatitude,
      mapLongitude,
      mapSearchRadius,
      setMapLatitude,
      setMapLongitude,
      setMapSearchRadius,
      isZoomChanged
    );
  };

  const handleMapPins = async (
    featuresAr: Feature[],
    longitude: string,
    latitude: string,
    mobileScreen: boolean,
    map: mapboxgl.Map,
    setHighlightId: (highlightData: {
      providerId: string;
      from: string;
    }) => void,
    disabledPinAction: boolean,
    updatePin:
      | ((content: ProviderLocation | FacilityLocationsResponse | null) => void)
      | undefined,
    enableMapEnhancements: boolean,
    enableMapViewLargeResults: boolean,
    setMapPinCoords: (coordinates: string[][]) => void,
    removeAllMarkers: () => void,
    setZoomLevel: (
      map: mapboxgl.Map | null,
      coordinates: (string[] | (string | undefined)[])[]
    ) => Promise<void>,
    loadResultLocationPins: (
      mobileScreen: boolean,
      map: mapboxgl.Map | null,
      features: any[],
      coordinates: (string[] | (string | undefined)[])[],
      setHighlightId: (highlightData: {
        providerId: string;
        from: string;
      }) => void,
      disabledPinAction: boolean,
      updatePin:
        | ((
            content: ProviderLocation | FacilityLocationsResponse | null
          ) => void)
        | undefined,
      enableMapEnhancements: boolean,
      enableMapViewLargeResults: boolean
    ) => Promise<void>
  ): Promise<void> => {
    if (!featuresAr?.length) {
      return;
    }
    const uniqueCoordinates = getUniqueCoordinates(
      featuresAr,
      longitude,
      latitude
    );

    setMapPinCoords(uniqueCoordinates);

    removeAllMarkers();
    await loadResultLocationPins(
      mobileScreen,
      map.current,
      featuresAr,
      uniqueCoordinates,
      setHighlightId,
      disabledPinAction,
      updatePin,
      enableMapEnhancements,
      false
    );
    await setZoomLevel(map.current, uniqueCoordinates);
  };

  const newMap = () => {
    let style = 'mapbox://styles/mapbox/streets-v12';
    mapboxgl.baseApiUrl = mapOnPremURL;
    const baseApiUrlEscaped = escapeRegExp(mapboxgl.baseApiUrl);
    Object.defineProperty(mapboxgl.config, 'API_URL_REGEX', {
      value: new RegExp(`^${baseApiUrlEscaped}`),
    });
    style = 'mapbox://styles/mapbox/streets-v11';
    map.current = new mapboxgl.Map({
      accessToken: mapOnPremKey,
      container: mapContainerRef.current,
      style,
      center: [longitude, latitude],
      zoom: Constants.DEVICE_LOCATION.ZOOM,
    });
    clearRoute(map.current, setRouteEndCoords);
    setHighlightId({ providerId: '', from: '' });
    map.current.on('load', async () => {
      await loadMap(
        map.current,
        setHighlightId,
        setPopupContent,
        enableMapEnhancements
      );

      loadUserLocationPin(map.current, longitude, latitude, isGehaTheme);

      const featuresAr = getFeaturesArray(
        enableMapPinRedesign,
        providerPins,
        providerResults,
        locationResults
      );

      await handleMapPins(
        featuresAr,
        longitude,
        latitude,
        mobileScreen,
        map,
        setHighlightId,
        disabledPinAction,
        updatePin,
        enableMapEnhancements,
        enableMapViewLargeResults,
        setMapPinCoords,
        removeAllMarkers,
        setZoomLevel,
        loadResultLocationPins
      );
      setIsMapLoaded(true);
    });

    // TODO 5/20 - This likely will no longer be needed depending on final map redesign decisions. Also likely will need additional fixes if it's actually enabled
    map.current.on('moveend', (e) => {
      if (
        enableMapPinRedesign &&
        enableMapDragRefreshResults &&
        !isOpenCompareShare &&
        'originalEvent' in e
      ) {
        const debounceSetMapCoords = debounce(doSetMapCoords, 500);
        debounceSetMapCoords(e.originalEvent.type === 'wheel');
      }
    });
  };

  useEffect(() => {
    setMapLatitude(undefined);
    setMapLongitude(undefined);
    setMapSearchRadius(undefined);
    removeAllMarkers();
    setMapPinCoords([]);

    return () => {
      setMapLatitude(undefined);
      setMapLongitude(undefined);
      setMapSearchRadius(undefined);
      removeAllMarkers();
      setMapPinCoords([]);
    };
  }, []);

  useEffect(() => {
    if (sortFilterOpen || allFiltersOpen) {
      handleCloseNav();
    }

    const intervalId = setInterval(() => {
      const currentSortFilterOpen = storage.session.get('sortFilterOpen');
      const currentAllFiltersOpen = storage.session.get('allFiltersOpen');
      if (currentSortFilterOpen !== sortFilterOpen) {
        setSortFilterOpen(currentSortFilterOpen);
      }
      if (currentAllFiltersOpen !== allFiltersOpen) {
        setAllFiltersOpen(currentAllFiltersOpen);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [sortFilterOpen, allFiltersOpen]);

  useEffect(() => {
    if (directions) {
      setMapDirections(directions);
      setShowStepInstructions(false);
      setCurrentRoute(0);
    }
  }, [directions]);

  useEffect(() => {
    setMapLatitude(undefined);
    setMapLongitude(undefined);
    setMapSearchRadius(undefined);
    removeAllMarkers();
    setMapPinCoords([]);
    if (isMapLoaded) {
      loadUserLocationPin(map.current, longitude, latitude, isGehaTheme);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (!isMapLoaded && mapboxgl?.config != undefined) {
      try {
        newMap();
      } catch (e) {
        throw e;
      }
    } else {
      removeAllMarkers();
      const featuresAr = getFeaturesArray(
        enableMapPinRedesign,
        providerPins,
        providerResults,
        locationResults
      );

      handleMapPins(
        featuresAr,
        longitude,
        latitude,
        mobileScreen,
        map,
        setHighlightId,
        disabledPinAction,
        updatePin,
        enableMapEnhancements,
        enableMapViewLargeResults,
        setMapPinCoords,
        removeAllMarkers,
        setZoomLevel,
        loadResultLocationPins
      ).then(() => {
        const uniqueCoordinates = getUniqueCoordinates(
          featuresAr,
          longitude,
          latitude
        );

        setMapPinCoords(uniqueCoordinates);
        setZoomLevel(map.current, uniqueCoordinates);
      });
    }
  }, [
    enableMapPinRedesign
      ? JSON.stringify(providerPins)
      : JSON.stringify(providerResults),
    JSON.stringify(locationResults),
    selectedItems,
    isOpenCompareShare,
  ]);

  useEffect(() => {
    const getMapRoute = async () => {
      const routeDirections = await getRoute(
        map.current,
        longitude,
        latitude,
        routeEndCoords[0],
        routeEndCoords[1],
        getUserLocation,
        false,
        navType,
        0,
        mapOnPremKey,
        mapOnPremURL
      );
      if (routeDirections) setMapDirections(routeDirections);
    };
    if (routeEndCoords[0]) {
      setCurrentRoute(0);
      getMapRoute();
    }
  }, [navType]);

  const hasRoute = routeEndCoords[0];

  useEffect(() => {
    setNavType('driving-traffic');
    if (!isWidget && !skipNavigationFocus)
      if (hasRoute === null) {
        refToBeFocusedOnDirectionsClose?.focus();
      } else {
        navigationContainerRef?.current?.focus();
      }
  }, [hasRoute]);

  useEffect(() => {
    if (map.current) map.current.resize();
  }, [closeSidePanel, mobileRouteView]);

  const handleCloseNav = () => {
    clearRoute(map.current, setRouteEndCoords, highlightId.providerId);
    setSelectedId(null);
    closeMapView?.();
  };

  const handleBackList = () => {
    adobeLinkTrackEvent({
      name: 'back',
      location: `body:view on list`,
      type: 'internal',
    });

    setNavigateToDirections(false);
    setExpandedProviderListView(true);
  };

  const mapControl = () => {
    const screen = enableMapEnhancements ? enableScreenZoom : mobileScreen;
    if (mobileScreen) {
      return (
        <MapControlMobile
          controlChildren={mobileMapControlChildren}
          enableMapEnhancements={enableMapEnhancements}
          isLocationsTab={isLocationsTab}
          map={map.current}
          mobileRouteView={!!routeEndCoords[0]}
        />
      );
    }

    return (
      <MapControlDesktop
        map={map.current}
        showZoomButtons={enableMapEnhancements && screen}
      />
    );
  };

  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const urlParams = new URLSearchParams(window.location.search);
  const choosePCPID = urlParams.get('choosePCPId') || '';

  const coverageTypeData = useCoverageType();
  const resultFilterName =
    coverageTypeData === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
      ? t('FILTER_BUTTONS.All_FILTERS')
      : t('CONTENT_FILTERS.ALL_FILTERS');

  const renderOldMap = () => (
    <NavigationContainer
      cssProps={{
        hasRoute,
        enableMapEnhancements: !enableMapFilterSortCTA,
      }}
      id="navigation-container-id"
      // @ts-expect-error This is a eslint error suggested by --fix command
      ref={navigationContainerRef}
      tabIndex={-1}
    >
      {hasRoute && mapDirections?.routes?.length && !isWidget ? (
        <MapDirections
          currentRoute={currentRoute}
          getDistanceString={getDistanceString}
          getDurationString={getDurationString}
          getNavTypeString={getNavTypeString}
          getStepDistanceString={getStepDistanceString}
          getStepIcon={getStepIcon}
          handleCloseNav={handleCloseNav}
          latitude={latitude}
          loadRoute={loadRoute}
          longitude={longitude}
          map={map}
          mapContainerRef={mapContainerRef}
          mapContainerTestId={mapContainerTestId}
          mapDirections={mapDirections}
          mobileScreen={mobileScreen}
          navType={navType}
          routeEndCoords={routeEndCoords}
          setCurrentRoute={setCurrentRoute}
          setNavType={setNavType}
          setShowStepInstructions={setShowStepInstructions}
          showStepInstructions={showStepInstructions}
        />
      ) : (
        <>
          {enableMapFilterSortCTA ? (
            <Flex alignItems="flex-end" justify="start" style={{ gap: '16px' }}>
              <ResultFilter
                color={additionalCss}
                icon="filter_list"
                name={resultFilterName}
                size="$md"
              />
              <ResultSort isMapView sectionType={sectionType} />
            </Flex>
          ) : (
            enableMapEnhancements &&
            !mobileScreen && (
              <Flex
                alignItems="flex-end"
                justify="start"
                style={{ gap: '16px' }}
              >
                <ResultFilter
                  color={additionalCss}
                  icon="filter_list"
                  name={resultFilterName}
                  size="$md"
                />
                <ResultSort isMapView sectionType={sectionType} />
              </Flex>
            )
          )}
        </>
      )}
    </NavigationContainer>
  );
  const renderNewMap = () => (
    <NavigationContainer2
      cssProps={{ isLocationsTab, mobileScreen, hasRoute }}
      id="navigation-container-id"
      // @ts-expect-error This is a eslint error suggested by --fix command
      ref={navigationContainerRef}
      tabIndex={-1}
    >
      {hasRoute && mapDirections?.routes?.length && !isWidget ? (
        <>
          {!enableMapFilterSortCTA &&
            !isLocationsTab &&
            !mobileScreen &&
            choosePCPID.length <= 0 &&
            !isWidget && (
              <Flex
                alignItems="flex-end"
                justify="start"
                style={{ gap: '16px' }}
              >
                <ResultFilter
                  color={additionalCss}
                  icon="filter_list"
                  name={resultFilterName}
                  size="$md"
                />
                <ResultSort isMapView sectionType={sectionType} />
              </Flex>
            )}
          <MapDirectionsContainer
            // @ts-expect-error This is a eslint error suggested by --fix command
            cssProps={{ isLocationsTab, mobileScreen }}
          >
            <MapDirections
              currentRoute={currentRoute}
              getDistanceString={getDistanceString}
              getDurationString={getDurationString}
              getNavTypeString={getNavTypeString}
              getStepDistanceString={getStepDistanceString}
              getStepIcon={getStepIcon}
              handleCloseNav={handleCloseNav}
              latitude={latitude}
              loadRoute={loadRoute}
              longitude={longitude}
              map={map}
              mapContainerRef={mapContainerRef}
              mapContainerTestId={mapContainerTestId}
              mapDirections={mapDirections}
              mobileScreen={mobileScreen}
              navType={navType}
              routeEndCoords={routeEndCoords}
              setCurrentRoute={setCurrentRoute}
              setNavType={setNavType}
              setShowStepInstructions={setShowStepInstructions}
              showStepInstructions={showStepInstructions}
            />
          </MapDirectionsContainer>
        </>
      ) : (
        <>
          {!enableMapFilterSortCTA &&
            enableMapEnhancements &&
            !mobileScreen &&
            !isLocationsTab &&
            !window.location.href?.includes('choose-pcp') &&
            choosePCPID.length <= 0 &&
            !isWidget && (
              <Flex
                alignItems="flex-end"
                css={{
                  '@media (max-width: 964px)': {
                    maxWidth: '375px',
                  },
                  '@media (max-width: 771px)': {
                    maxWidth: '180px',
                  },
                }}
                justify="start"
                style={{ gap: '16px' }}
              >
                <ResultFilter
                  color={additionalCss}
                  icon="filter_list"
                  name={resultFilterName}
                  size="$md"
                />
                <ErrorBoundary>
                  <ChipFilters
                    enableMapFilterSortCTA={enableMapFilterSortCTA}
                    previousLocation={previousLocation}
                    sectionType={sectionType || ''}
                    setPreviousLocation={setPreviousLocation}
                  />
                </ErrorBoundary>
                <ResultSort isMapView sectionType={sectionType} />
              </Flex>
            )}
        </>
      )}
    </NavigationContainer2>
  );

  return (
    <React.Fragment>
      {!mobileScreen && enableMapViewHideOption && expandedProviderListView && (
        <Flex css={{ margin: '24px 0 24px 16px' }}>
          <Link
            data-auto-testid="back-to-provider-list-view"
            data-testid="back-to-provider-list-view"
            isStandardAnchor
            onClick={() => handleBackList()}
          >
            <IconSymbol
              color="$core.color.brand.60"
              css={{ width: '20px', height: '20px', marginRight: '4px' }}
              icon="arrow_back"
              title="Back_to-results"
            />
            {t('BACK_TO_RESULTS')}
          </Link>
        </Flex>
      )}
      <MapContainer
        // @ts-expect-error This is a eslint error suggested by --fix command
        cssProps={{
          enableMapEnhancements,
          fullWidth,
          isOpenCompareShare,
          mobileRouteView,
          hasRoute,
          isWidget,
          isGehaTheme,
        }}
      >
        <div
          className="map-container"
          data-auto-testid={mapContainerTestId}
          data-testid={mapContainerTestId}
          ref={mapContainerRef}
        />
        {mapControl()}
        {enableMapEnhancements ? renderNewMap() : renderOldMap()}
      </MapContainer>
      <ClusterCarousel
        data={[]}
        headers={headers}
        search={search}
        sectionType={sectionType}
        selectLocation={selectLocation}
      />
    </React.Fragment>
  );
};
